// Input — 1:1 port of legacy `src/ui/Primitives.jsx:54–82`.
import { forwardRef, useState, type CSSProperties, type KeyboardEventHandler } from 'react';
import { Icon, type IconName } from './Icon.js';
import { useFormRowControl } from './FormRow.js';

export interface InputProps {
  value: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  icon?: IconName;
  trailingIcon?: IconName;
  type?: string;
  invalid?: boolean;
  style?: CSSProperties;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  // WHY (#1437): optional blur hook so a controlled decimal field can
  //   canonicalize its text buffer on blur. The native handler still
  //   flips focus state first; callers that omit it compile unchanged.
  onBlur?: () => void;
  readOnly?: boolean;
  monospace?: boolean;
  ariaLabel?: string;
  // WHY: forwarded as-is to the underlying <input> so a password manager
  // sees the correct hint (e.g. "new-password" vs "current-password").
  autoComplete?: string | undefined;
  // WHY: forwarded to the native <input maxLength=…> so the browser
  // hard-caps user typing/paste to the same upper bound the wire schema
  // enforces. Without this, a long paste turns a list request into a
  // 400 from the shared `q.max(200)` rule, which the UI surfaces as an
  // empty table because the list has no per-error branch.
  maxLength?: number | undefined;
  // WHY (#1439): mount-only focus for newly-added positions. React's
  //   autoFocus fires once on mount; existing rows are unaffected because
  //   autoFocus is never re-fired after the element is already in the DOM.
  autoFocus?: boolean;
}

/**
 * @aiPurpose text control that pairs with {@link FormRow} for its accessible
 *   name, description and validity state.
 * @aiInvariant the ref is forwarded to the native `<input>` so a form library
 *   (React Hook Form) can focus the field on an invalid submit.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    value,
    onChange,
    placeholder,
    icon,
    trailingIcon,
    type = 'text',
    invalid,
    style,
    onKeyDown,
    onBlur,
    readOnly,
    monospace,
    ariaLabel,
    autoComplete,
    maxLength,
    autoFocus,
  },
  ref,
) {
  const [focus, setFocus] = useState(false);
  // WHY: `invalid` stays the sole driver of the error border so existing
  // consumers keep their current look; only `aria-invalid` is inherited from
  // the row, which is what assistive technology reads.
  const field = useFormRowControl({ invalid });
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        height: 'var(--control-h)',
        padding: '0 10px',
        borderRadius: 'var(--r-2)',
        border: `1px solid ${
          invalid ? 'var(--status-error-fg)' : focus ? 'var(--accent-1)' : 'var(--border-1)'
        }`,
        background: 'var(--bg-1)',
        boxShadow: focus && !invalid ? `0 0 0 3px var(--accent-ring)` : 'none',
        transition:
          'border-color var(--dur-1) var(--ease-out), box-shadow var(--dur-1) var(--ease-out)',
        ...style,
      }}
    >
      {icon && <Icon name={icon} size={14} style={{ color: 'var(--fg-3)' }} />}
      <input
        ref={ref}
        id={field.id}
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange && onChange(e.target.value)}
        onKeyDown={onKeyDown}
        readOnly={readOnly}
        aria-label={ariaLabel}
        aria-describedby={field.describedBy}
        aria-invalid={field.invalid ? true : undefined}
        placeholder={placeholder}
        autoComplete={autoComplete}
        maxLength={maxLength}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        onFocus={() => setFocus(true)}
        onBlur={() => {
          setFocus(false);
          onBlur?.();
        }}
        style={{
          border: 0,
          outline: 0,
          background: 'transparent',
          width: '100%',
          fontFamily: monospace ? 'var(--font-mono)' : 'var(--font-ui)',
          // WHY (#3039): the caller's `style.fontSize` has to reach the NATIVE
          //   input, not just the shell — mobile WebKit auto-zooms a focused
          //   field whose own font is under 16 px, and that zoom disables the
          //   narrow sheet's visual-viewport sizing.
          fontSize: style?.fontSize ?? (monospace ? 'var(--fz-12)' : 'var(--fz-13)'),
          color: 'var(--fg-1)',
          fontVariantNumeric: 'tabular-nums',
        }}
      />
      {trailingIcon && <Icon name={trailingIcon} size={14} style={{ color: 'var(--fg-3)' }} />}
    </div>
  );
});
